import { format } from 'date-fns';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCampaignStore } from '@/stores/campaignStore';
import { useAuth } from '@/contexts/AuthContext';
import { useCalendarData } from '@/hooks/useCalendarData';
import { generateMonthCalendarDays, isSameMonth } from '@/lib/calendarUtils';
import { MonthDayCell } from '../shared/MonthDayCell';
import { CalendarTimezoneBanner } from '../CalendarTimezoneBanner';

/** Day names for the header row (Sunday start) */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Month calendar view with 7-column grid.
 * - Header row shows day names
 * - 6 rows of day cells (42 total days)
 * - Shows event indicators for shifts, tasks, and external calendar events
 * - Click to navigate to week view
 */
export function MonthView() {
  const visibleStartDate = useCalendarStore((state) => state.visibleStartDate);
  const timeZoneId = useCampaignStore((s) => s.selectedCampaign?.timeZoneId);
  const setView = useCalendarStore((state) => state.setView);
  const setSelectedDate = useCalendarStore((state) => state.setSelectedDate);
  const activeFilters = useCalendarStore((state) => state.activeFilters);
  const showDelegated = useCalendarStore((state) => state.showDelegated);
  const { user } = useAuth();

  const calendarDays = generateMonthCalendarDays(visibleStartDate);

  const {
    getShiftsForDate,
    getTasksForDate,
    getAllCalendarEventsForDate,
    getKeyDatesForDate,
  } = useCalendarData(calendarDays[0], calendarDays[calendarDays.length - 1], timeZoneId);

  const isFilteringAll = activeFilters.includes('all');
  const categoryFilters = activeFilters.filter((f) => f !== 'shifts' && f !== 'calendar');
  const showShifts = isFilteringAll || activeFilters.includes('shifts');
  const showCalendarEvents = isFilteringAll || activeFilters.includes('calendar');

  function handleDayClick(date: Date): void {
    setSelectedDate(date);
    setView('week');
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <CalendarTimezoneBanner />
      {/* Day names header */}
      <div className="grid grid-cols-7 border-b border-dotted border-gray-200 bg-white">
        {DAY_NAMES.map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-500 border-r border-dotted border-gray-200 last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid - 6 rows of 7 days */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 bg-white">
        {calendarDays.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isCurrentMonth = isSameMonth(date, visibleStartDate);

          const allTasks = getTasksForDate(dateStr);
          const categoryFilteredTasks = isFilteringAll
            ? allTasks
            : allTasks.filter((t) => {
                if (categoryFilters.length === 0) return true;
                if (categoryFilters.includes('uncategorized') && !t.categoryId) return true;
                if (t.categoryId && categoryFilters.includes(t.categoryId)) return true;
                return false;
              });

          // Apply delegated filter
          const filteredTasks = showDelegated
            ? categoryFilteredTasks
            : categoryFilteredTasks.filter(
                (t) => !(t.createdById === user?.id && t.assignedToId && t.assignedToId !== user?.id)
              );

          return (
            <MonthDayCell
              key={date.toISOString()}
              date={date}
              onClick={() => handleDayClick(date)}
              shifts={showShifts ? getShiftsForDate(dateStr) : []}
              tasks={filteredTasks}
              calendarEvents={showCalendarEvents ? getAllCalendarEventsForDate(dateStr) : []}
              keyDates={getKeyDatesForDate(dateStr)}
              isCurrentMonth={isCurrentMonth}
              timeZoneId={timeZoneId}
            />
          );
        })}
      </div>
    </div>
  );
}

export default MonthView;
